import { ActionButton, Button } from "@jhool-io/fe-components";
import { useIsMutating } from "@tanstack/react-query";
import { isFuture } from "date-fns";
import { cn } from "../../../../../utils/helpers";
import { useAppSelector } from "../../../../../hooks/useRedux";
import { useNoteEditContext } from "../../../context/notes.context";
import { NoteValidationSummary } from "../../NoteValidationSummary";

interface EditNoteFooterProps {
    onSaveButtonClick: () => void;
    noteDOS: string | null;
    onSubmitButtonClick: () => void;
    submitBtnIsDisabled?: boolean;
    submitBtnType?: "submit" | "button";
    submitButtonLabel?: string;
}

export default function EditNoteFooter({
    onSaveButtonClick,
    noteDOS,
    onSubmitButtonClick,
    submitBtnIsDisabled,
    submitBtnType = "submit",
    submitButtonLabel = "Submit for review",
}: EditNoteFooterProps) {
    const { isOpen } = useAppSelector((state) => state.nav);
    const { signAttemptRef } = useNoteEditContext();

    const isEditDraftNoteMutating = useIsMutating(["edit-note"]);
    const isSignNoteMutating = useIsMutating(["sign-note"]);

    // function to disable button when dos is in the future
    const isTheDateOfServiceInTheFuture = () => {
        if (noteDOS) {
            return isFuture(new Date(noteDOS));
        }
        return false;
    };

    const handleSubmitButtonClick = () => {
        if (signAttemptRef) signAttemptRef.current = true;
        onSubmitButtonClick();
    };

    return (
        <div
            className={cn(
                "fixed bottom-0 right-0 flex flex-wrap items-center gap-x-5 gap-y-3 justify-end px-[--padding-page] bg-[--bg-table] pt-8 sm:pb-8 pb-[50px] w-[calc(100%-var(--sidebar-width))] backdrop-blur-[10px]",
                {
                    "w-full": !isOpen,
                }
            )}
        >
            <NoteValidationSummary />
            <Button
                type="button"
                variant="secondary"
                aria-label="save as draft"
                disabled={isEditDraftNoteMutating > 0 || isSignNoteMutating > 0}
                onClick={onSaveButtonClick}
            >
                Save as draft
            </Button>
            {isTheDateOfServiceInTheFuture() ? (
                <ActionButton
                    form="edit-note-form"
                    type={submitBtnType ?? "submit"}
                    aria-label={submitButtonLabel}
                    actionTitle="Note with future date of service can't be submitted"
                    disabled
                    onClick={handleSubmitButtonClick}
                >
                    {submitButtonLabel}
                </ActionButton>
            ) : (
                <Button
                    form="edit-note-form"
                    type={submitBtnType ?? "submit"}
                    aria-label={submitButtonLabel}
                    disabled={
                        submitBtnIsDisabled ||
                        isEditDraftNoteMutating > 0 ||
                        isSignNoteMutating > 0
                    }
                    onClick={handleSubmitButtonClick}
                >
                    {submitButtonLabel}
                </Button>
            )}
        </div>
    );
}
